import type { EventDetailDto, SportsEventDetailDto } from '@/lib/models';
import { MarketPageHeader } from '../BinaryMarkets/MarketPageHeader';
import { MarketChartContainer } from '../BinaryMarkets/MarketChartContainer';
import { MarketSections } from '../BinaryMarkets/MarketSections';
import { BuyInPopup } from '../BinaryMarkets/BuyInPopup';
import { useBuyTarget } from '../BinaryMarkets/useBuyTarget';
import { sportsColor } from '@/lib/sports-market';
import { SportsMoneylineBlock } from './SportsMoneylineBlock';
import { SportsLineLadderBlock } from './SportsLineLadderBlock';
import { SportsTeamLegend } from './SportsTeamLegend';

export interface SportsMarketDetailProps {
  /** The event being viewed (a SPORTS MATCH — `event.sports` is present). */
  event: EventDetailDto;
  /** The event's resolved sports blocks (moneyline + spread/total). */
  sports: SportsEventDetailDto;
}

/** `June 14 @ 8:30 PM` from an ISO timestamp, or undefined when absent/unparseable. */
function formatStartTime(iso: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} @ ${time}`;
}

/**
 * Sports event detail body (ARC-80 §5): matchup header (title + start time + team
 * legend + stat strip), a multi-line price chart of the moneyline, the Moneyline /
 * Spreads / Totals game-line blocks, then the shared collapsible sections. Tapping
 * any answer/team/Over-Under opens the inherited buy-in popup pre-set to it (§7).
 */
export function SportsMarketDetail({ event, sports }: SportsMarketDetailProps) {
  const { market, initialSide, onOpenBuy, close } = useBuyTarget(event.name);

  const options = event.markets.flatMap((m) => m.options);
  // Chart the moneyline legs (one line per team), coloured by the team colour.
  const chartOutcomes = (sports.moneyline?.outcomes ?? []).map((o) => ({
    id: o.outcomeId,
    outcomeId: o.outcomeId,
    label: o.label,
    color: sportsColor(o.color, o.side),
    currentPriceCents: o.yes.priceCents,
  }));
  const categoryLabel = sports.league ? `Sports – ${sports.league}` : undefined;
  const startTime = formatStartTime(event.startTime);

  return (
    <div className="flex flex-col gap-5">
      <MarketPageHeader
        event={event}
        categoryLabel={categoryLabel}
        subtitle={
          startTime ? <span className="text-[14px] font-light leading-normal text-ink">{startTime}</span> : undefined
        }
        beforeStats={sports.moneyline ? <SportsTeamLegend moneyline={sports.moneyline} /> : undefined}
      />
      {chartOutcomes.length > 0 && <MarketChartContainer outcomes={chartOutcomes} />}
      {sports.moneyline && <SportsMoneylineBlock moneyline={sports.moneyline} onOpenBuy={onOpenBuy} />}
      {sports.spread && <SportsLineLadderBlock title="Spreads" ladder={sports.spread} onOpenBuy={onOpenBuy} />}
      {sports.total && <SportsLineLadderBlock title="Totals" ladder={sports.total} onOpenBuy={onOpenBuy} />}
      {/* Result narrows to the moneyline: the match winner, not every prop market's outcome. */}
      <MarketSections
        event={event}
        options={options}
        resultMarkets={event.markets.filter((m) => m.id === sports.moneyline?.marketId)}
      />

      <BuyInPopup market={market} initialSide={initialSide} surface="event.detail" onClose={close} />
    </div>
  );
}
